import React, { useEffect, useRef, useState } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Link,
  Navigate,
  useLocation,
  useParams
} from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  FaBars,
  FaMapMarkedAlt,
  FaTimes,
} from "react-icons/fa";
import Home from "./components/home";
import logo from "./images/cape.png";
import PreviewMap from "./components/previewMap";
import PreviewMapForm from "./components/previewMapForm";
import DemoMap from "./components/demoMap";
import LanguageSwitcher from "./components/LanguageSwitcher";
import CaregiverMarketingPage from "./components/caregiverMarketingPage";
import ActivatedMap from "./components/activatedPatientMap";

const LegacyPreviewMapRedirect = ({ includeLocationStep = false }) => {
  const { caregiverID = "" } = useParams();
  const location = useLocation();
  const suffix = includeLocationStep ? "/location" : "";

  return (
    <Navigate
      to={`/realMap/edit/${caregiverID}${suffix}${location.search || ""}`}
      replace
    />
  );
};

const AppContent = () => {
  const { t } = useTranslation("common");
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const headerRef = useRef(null);
  const [headerOffset, setHeaderOffset] = useState(132);
  const workerId = "";
  const isMarketingPage = location.pathname === "/caregiver-marketing";

  const navLinks = [
    { label: "Get Started", to: "/realMap", icon: <FaMapMarkedAlt /> },
    { label: "Practice Map", to: "/previewMap", icon: <FaMapMarkedAlt /> }
  ];

  useEffect(() => {
    if (isMarketingPage) return undefined;

    const updateHeaderOffset = () => {
      const nextOffset = (headerRef.current?.offsetHeight || 0) + 28;
      setHeaderOffset((previous) =>
        previous === nextOffset ? previous : nextOffset
      );
    };

    updateHeaderOffset();

    const frame = window.requestAnimationFrame(updateHeaderOffset);
    const resizeObserver =
      typeof ResizeObserver !== "undefined" && headerRef.current
        ? new ResizeObserver(updateHeaderOffset)
        : null;

    if (resizeObserver && headerRef.current) {
      resizeObserver.observe(headerRef.current);
    }

    window.addEventListener("resize", updateHeaderOffset);

    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener("resize", updateHeaderOffset);
      resizeObserver?.disconnect();
    };
  }, [isMarketingPage, location.pathname, menuOpen]);

  return (
    <>
      <style>{responsiveStyles}</style>

      {!isMarketingPage && (
        <header ref={headerRef} style={headerWrap}>
          <div style={headerShell} className="app-header-shell">
            <Link
              to="/"
              style={brandLink}
              onClick={() => setMenuOpen(false)}
            >
              <div style={logoWrap} className="app-logo-wrap">
                <img src={logo} alt="UberCARE logo" style={logoStyle} />
              </div>

              <div className="app-brand-copy">
                <div style={brandTitle} className="app-brand-title">{t("nav.brand")}</div>
                <div style={brandSubtitle} className="app-brand-subtitle">Caregiver care network</div>
              </div>
            </Link>

            <button
              type="button"
              style={menuToggle}
              className="app-mobile-toggle"
              onClick={() => setMenuOpen((prev) => !prev)}
              aria-label={menuOpen ? "Close menu" : "Open menu"}
            >
              {menuOpen ? <FaTimes /> : <FaBars />}
            </button>

            <div
              style={navArea}
              className={`app-nav-area${menuOpen ? " app-nav-open" : ""}`}
            >
              <nav style={navLinksWrap}>
                {navLinks.map((item) => {
                  const active =
                    item.to === "/"
                      ? location.pathname === "/"
                      : location.pathname.startsWith(item.to);

                  return (
                    <Link
                      key={item.to}
                      to={item.to}
                      onClick={(e) => {
                        setMenuOpen(false);
                        if (item.onClick) item.onClick(e);
                      }}
                      style={{
                        ...linkStyle,
                        ...(active ? activeLinkStyle : {})
                      }}
                    >
                      <span style={linkIcon}>{item.icon}</span>
                      {item.label}
                    </Link>
                  );
                })}
              </nav>

              <div style={actionWrap}>
                <LanguageSwitcher userId={workerId} role="worker" />

                <Link
                  to="/realMap"
                  style={primaryAction}
                  onClick={() => setMenuOpen(false)}
                >
                  <FaMapMarkedAlt />
                  Get Started
                </Link>
              </div>
            </div>
          </div>
        </header>
      )}

      <main
        className="app-main"
        style={
          isMarketingPage ? marketingContentOffset : getContentOffset(headerOffset)
        }
      >
        <Routes>
          <Route path="/" element={<Home />} />
          <Route
            path="/caregiver-marketing"
            element={<CaregiverMarketingPage />}
          />
          <Route path="/remoteLogin" element={<Navigate to="/realMap" replace />} />
          <Route path="/remoteRegister" element={<Navigate to="/realMap" replace />} />
          <Route path="/logout" element={<Navigate to="/realMap" replace />} />

          <Route
            path="/remoteProfileViewer"
            element={<Navigate to="/realMap" replace />}
          />

          <Route
            path="/remoteProfileEdit"
            element={<Navigate to="/realMap" replace />}
          />

          <Route
            path="/workerHome"
            element={<Navigate to="/realMap" replace />}
          />

          <Route
            path="/activatedElderMap"
            element={<Navigate to="/realMap" replace />}
          />
          <Route path="/appointments/:caregiverID" element={<ActivatedMap />} />

          <Route path="/previewMap" element={<DemoMap />} />
          <Route path="/realMap" element={<PreviewMap />} />
          <Route path="/realMap/add" element={<Navigate to="/realMap" replace />} />
          <Route path="/realMap/add/location" element={<Navigate to="/realMap" replace />} />
          <Route path="/realMap/edit/:caregiverID" element={<PreviewMapForm />} />
          <Route
            path="/realMap/edit/:caregiverID/location"
            element={<PreviewMapForm />}
          />
          <Route path="/previewMap/add" element={<Navigate to="/realMap" replace />} />
          <Route
            path="/previewMap/add/location"
            element={<Navigate to="/realMap" replace />}
          />
          <Route
            path="/previewMap/edit/:caregiverID"
            element={<LegacyPreviewMapRedirect />}
          />
          <Route
            path="/previewMap/edit/:caregiverID/location"
            element={<LegacyPreviewMapRedirect includeLocationStep />}
          />

          <Route
            path="/workermap"
            element={<Navigate to="/realMap" replace />}
          />
        </Routes>
      </main>
    </>
  );
};

const App = () => {
  return (
    <Router basename="/worker">
      <AppContent />
    </Router>
  );
};

const responsiveStyles = `
  .app-main {
    min-height: 100vh;
    background:
      radial-gradient(circle at top left, rgba(255, 210, 74, 0.16), transparent 24%),
      linear-gradient(180deg, #eef5f8 0%, #dfe9f2 100%);
  }

  .app-mobile-toggle {
    display: none !important;
  }

  .app-nav-area a,
  .app-nav-area button {
    min-height: 42px;
  }

  @media (max-width: 980px) {
    .app-header-shell {
      align-items: flex-start !important;
      flex-wrap: wrap !important;
    }

    .app-mobile-toggle {
      display: inline-flex !important;
    }

    .app-nav-area {
      display: none !important;
      width: 100%;
      flex-direction: column !important;
      gap: 16px !important;
      padding-top: 18px;
      border-top: 1px solid rgba(255, 255, 255, 0.18);
    }

    .app-nav-open {
      display: flex !important;
    }

    .app-nav-area nav,
    .app-nav-area > div {
      width: 100% !important;
      justify-content: stretch !important;
    }

    .app-nav-area a,
    .app-nav-area button {
      flex: 1 1 180px !important;
      justify-content: center !important;
      background: rgba(255, 255, 255, 0.12) !important;
      color: #ffffff !important;
      border: 1px solid rgba(255, 255, 255, 0.18) !important;
      box-shadow: inset 0 1px 0 rgba(255,255,255,0.08);
    }
  }

  @media (max-width: 560px) {
    .app-header-shell {
      padding: 10px !important;
      border-radius: 18px !important;
      gap: 10px !important;
    }

    .app-logo-wrap {
      width: 58px !important;
      height: 58px !important;
      border-radius: 14px !important;
    }

    .app-logo-wrap img {
      width: 42px !important;
      height: 42px !important;
    }

    .app-brand-copy {
      min-width: 0;
    }

    .app-brand-title {
      font-size: 17px !important;
      line-height: 1.1 !important;
      overflow-wrap: anywhere;
    }

    .app-brand-subtitle {
      display: none !important;
    }

    .app-mobile-toggle {
      width: 48px !important;
      height: 48px !important;
      background: #ffd24a !important;
      color: #14324c !important;
      border: 1px solid rgba(255,255,255,0.58) !important;
      box-shadow: 0 10px 22px rgba(0,0,0,0.22) !important;
    }

    .app-nav-area {
      gap: 10px !important;
      padding-top: 10px !important;
    }

    .app-nav-area nav,
    .app-nav-area > div {
      display: grid !important;
      grid-template-columns: 1fr !important;
      gap: 8px !important;
    }

    .app-nav-area a,
    .app-nav-area button {
      width: 100% !important;
      min-height: 50px !important;
      border-radius: 12px !important;
      font-size: 15px !important;
      font-weight: 900 !important;
    }
  }

  @media (max-width: 380px) {
    .app-brand-title {
      font-size: 15px !important;
    }
  }
`;

const headerWrap = {
  position: "fixed",
  top: "8px",
  left: "50%",
  transform: "translateX(-50%)",
  width: "min(1320px, calc(100% - 18px))",
  zIndex: 1000
};

const headerShell = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "16px",
  padding: "10px 16px",
  borderRadius: "24px",
  background: "linear-gradient(135deg, rgba(9, 52, 82, 0.98), rgba(15, 88, 128, 0.97))",
  border: "1px solid rgba(255, 255, 255, 0.24)",
  boxShadow: "0 22px 48px rgba(8, 34, 55, 0.22)",
  backdropFilter: "blur(18px)"
};

const brandLink = {
  display: "flex",
  alignItems: "center",
  gap: "12px",
  textDecoration: "none",
  minWidth: 0
};

const logoWrap = {
  width: "92px",
  height: "92px",
  borderRadius: "16px",
  background: "linear-gradient(135deg, #114f77 0%, #1d6b8f 100%)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0
};

const logoStyle = {
  width: "64px",
  height: "64px",
  objectFit: "contain"
};

const brandTitle = {
  color: "#ffffff",
  fontWeight: "800",
  fontSize: "22px",
  lineHeight: 1
};

const brandSubtitle = {
  marginTop: "4px",
  color: "rgba(255, 255, 255, 0.72)",
  fontSize: "11px",
  fontWeight: "700",
  letterSpacing: "0.08em",
  textTransform: "uppercase"
};

const menuToggle = {
  width: "46px",
  height: "46px",
  borderRadius: "14px",
  border: "1px solid rgba(255,255,255,0.34)",
  background: "#ffd24a",
  color: "#14324c",
  cursor: "pointer",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "18px",
  boxShadow: "0 10px 22px rgba(0,0,0,0.18)",
  flexShrink: 0
};

const navArea = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "14px",
  flex: 1,
  minWidth: 0
};

const navLinksWrap = {
  display: "flex",
  alignItems: "center",
  gap: "8px",
  flexWrap: "wrap"
};

const linkStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: "8px",
  textDecoration: "none",
  color: "rgba(255, 255, 255, 0.86)",
  padding: "8px 12px",
  borderRadius: "14px",
  fontSize: "13px",
  fontWeight: "700",
  background: "rgba(255, 255, 255, 0.08)",
  border: "1px solid rgba(255, 255, 255, 0.1)"
};

const activeLinkStyle = {
  color: "#ffffff",
  background: "rgba(255, 255, 255, 0.14)"
};

const linkIcon = {
  fontSize: "14px"
};

const actionWrap = {
  display: "flex",
  alignItems: "center",
  gap: "10px",
  flexWrap: "wrap",
  justifyContent: "flex-end"
};

const primaryAction = {
  display: "inline-flex",
  alignItems: "center",
  gap: "8px",
  textDecoration: "none",
  padding: "8px 14px",
  borderRadius: "14px",
  border: "none",
  background: "linear-gradient(135deg, #2b8b7f 0%, #3aa697 100%)",
  color: "#ffffff",
  fontSize: "13px",
  fontWeight: "800"
};

const getContentOffset = (headerOffset) => ({
  paddingTop: `${headerOffset}px`
});

const marketingContentOffset = {
  paddingTop: 0
};

export default App;
